/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_TINYMCE_EDITOR
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Split BroadcastSignatureEditor into smaller files to meet line limits.
 * Extracted TinyMCE 6.0 configuration to a separate utility.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/broadcast-signature-editor/BroadcastSignatureEditor.tsx
 */
import React, { forwardRef, useImperativeHandle, useRef, type Ref } from "react";
import { Editor as TinyMCEEditor } from "@tinymce/tinymce-react";
import { getTinyMCEConfig } from "./utils/tinymceConfig";

export interface BroadcastSignatureEditorRef {
  getHTML: () => string;
  forceUpdateWithStyles: () => void;
}

interface BroadcastSignatureEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const BroadcastSignatureEditor = forwardRef(function BroadcastSignatureEditorInner(
  props: BroadcastSignatureEditorProps,
  ref: Ref<BroadcastSignatureEditorRef>,
) {
  const { value, onChange, placeholder } = props;
  const editorRef = useRef<any | null>(null);

  const config = React.useMemo(() => getTinyMCEConfig(placeholder || ""), [placeholder]);

  useImperativeHandle(
    ref,
    () => ({
      getHTML: () => {
        if (editorRef.current) {
          const content: string = editorRef.current.getContent({
            format: "html",
            no_events: true,
            save: false,
            getInner: true,
          });
          return content;
        }
        return "";
      },
      forceUpdateWithStyles: () => {},
    }),
    [],
  );

  const handleEditorInit = (_evt: unknown, editor: any) => {
    editorRef.current = editor;
  };

  return (
    <TinyMCEEditor
      apiKey={process.env.REACT_APP_TINYMCE_API_KEY || "no-api-key"}
      value={value}
      onInit={handleEditorInit}
      onEditorChange={(content: string) => {
        if (content !== value) {
          onChange(content);
        }
      }}
      init={config as any}
    />
  );
});

export default BroadcastSignatureEditor;
